import { NumberFormatter } from '@mantine/core';
import { DataBadge } from 'components/common/DataBadge';
import { EOBStack } from 'components/common/EOBStack';
import { ICD9Badge } from 'components/common/ICD9Badge';
import { StatusBadge } from 'components/common/StatusBadge';
import { Debit, EOB, Patient, Payor } from 'interfaces';
import { DateTime } from 'luxon';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useCurrentSiteStore } from 'store';
import { formatDateOnly, formatDateTime } from 'utils/dateUtils';
import { calculateNetAmount, calculateOverheadFee } from 'utils/overheadUtils';

interface Transaction extends Debit {
  patient?: Patient;
}

export const TransactionColumns = ({ payors }: { payors: Payor[] }) => {
  const { timezone } = useCurrentSiteStore();

  const columns: MRT_ColumnDef<Transaction>[] = [
    {
      accessorKey: 'claim.serviceStart',
      header: 'Date of Service',
      accessorFn: (row) =>
        formatDateTime(row?.claim?.serviceStart, {
          timezone,
          format: DateTime.DATE_SHORT,
          fallback: 'N/A',
        }),
    },
    {
      header: 'Payment Date',
      accessorKey: 'createdAt',
      accessorFn: (row) =>
        formatDateTime(row?.createdAt, {
          timezone,
          format: DateTime.DATE_SHORT,
          fallback: 'N/A',
        }),
    },
    {
      header: 'Submission Date',
      accessorKey: 'submission.submittedAt',
      accessorFn: (row) => {
        const submittedAt =
          row.claim?.submissions?.[row.claim?.submissions.length - 1]
            ?.submittedAt;
        return formatDateTime(submittedAt, {
          timezone,
          format: DateTime.DATE_SHORT,
          fallback: 'N/A',
        });
      },
    },
    {
      header: 'Amount Submitted',
      accessorFn: (row) => row.requestedAmount,
      Cell: ({ cell }) => (
        <NumberFormatter
          prefix="$ "
          value={cell.getValue() as string}
          thousandSeparator
        />
      ),
    },
    {
      accessorKey: 'paidAmount',
      header: 'Amount Received',
      accessorFn: (row) => row.paidAmount,
      Cell: ({ cell }) => (
        <NumberFormatter
          prefix="$ "
          value={cell.getValue() as string}
          thousandSeparator
        />
      ),
    },
    {
      header: 'Overhead Paid',
      accessorFn: (row) =>
        calculateOverheadFee(row?.paidAmount, row?.claim?.overhead as number),
      Cell: ({ cell }) => (
        <NumberFormatter
          prefix="$ "
          value={cell.getValue() as string}
          thousandSeparator
        />
      ),
    },
    {
      header: 'Provider',
      accessorKey: 'provider',
      accessorFn: (row) =>
        row.provider?.firstName + ' ' + row.provider?.lastName,
    },
    {
      header: 'Paid To Provider',
      accessorFn: (row) =>
        calculateNetAmount(row?.paidAmount, row?.claim.overhead as number),
      Cell: ({ cell }) => (
        <NumberFormatter
          prefix="$ "
          value={cell.getValue() as string}
          thousandSeparator
        />
      ),
    },
    {
      header: 'Patient',
      accessorFn: (row) =>
        row?.claim?.patient
          ? `${row.claim.patient.firstName} ${row.claim.patient.lastName}`
          : `${row.patient.firstName} ${row.patient.lastName}`,
    },
    {
      header: 'DOB',
      accessorFn: (row) => {
        const date = row?.claim?.patient
          ? row.claim.patient.dateOfBirth
          : row.patient.dateOfBirth;
        return formatDateOnly(date, { timezone, fallback: 'N/A' });
      },
    },

    {
      accessorKey: 'tariff',
      header: 'Tariff',
      accessorFn: (row) => {
        if (!row?.tariff) return 'N/A';
        if (row?.tariff?.type === 'Base') {
          return (
            <DataBadge
              key={row.tariff.id}
            >{`${row.tariff.prefix}${row.tariff.code}`}</DataBadge>
          );
        } else {
          return (
            <StatusBadge
              key={row.tariff.id}
            >{`${row.tariff.prefix}${row.tariff.code}`}</StatusBadge>
          );
        }
      },
    },
    {
      accessorKey: 'tariff.icd9',
      header: 'ICD9',
      accessorFn: (row) => (
        <ICD9Badge key={row.tariff?.id} icd9={row.tariff?.icd9} />
      ),
    },
    {
      accessorKey: 'claim.claimNumber',
      header: 'Claim Number',
      accessorFn: (row) => row?.claim?.claimNumber,
    },
    {
      accessorKey: 'eobs',
      header: 'EOB(s)',
      accessorFn: (row) => (
        <EOBStack eobs={row.eobs as Array<EOB & { tariffID: string }>} />
      ),
    },
    {
      header: 'Payor',
      accessorFn: (row) => {
        const payor = payors?.find((payor) => payor?.id === row?.payorID);
        return payor?.name;
      },
      size: 80,
    },
  ];
  return columns;
};
